import { SquareIcon } from "@kalyzee/kalyzee-ui";
import { useEffect, useMemo } from "react";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { DEFAULT_LAYOUTS, isVerticalStripLayout, LayoutType } from "../../interfaces/ui";

const VARIANT_BY_LAYOUT: Record<LayoutType, React.ComponentType<{ containerSize?: number; iconSize?: number; selected?: boolean; onClick?: () => void }>> = {
  [LayoutType.Top]: SquareIcon.Variants.LayoutTop,
  [LayoutType.Bottom]: SquareIcon.Variants.LayoutBottom,
  [LayoutType.Left]: SquareIcon.Variants.LayoutLeft,
  [LayoutType.Right]: SquareIcon.Variants.LayoutRight,
  [LayoutType.Full]: SquareIcon.Variants.LayoutFull,
};

export interface LayoutSelectorProps {
  className?: string;
  style?: React.CSSProperties;
  size?: number;
  iconSize?: number;
  /** Layouts to show. Defaults to {@link DEFAULT_LAYOUTS}. */
  layouts?: ReadonlyArray<LayoutType>;
  /** Fallback layout when current is filtered out. Defaults to first available. */
  defaultLayout?: LayoutType;
  /** Hide vertical-strip layouts on mobile (default true). */
  hideVerticalOnMobile?: boolean;
  orientation?: "horizontal" | "vertical";
}

export const LayoutSelector = ({
  className,
  style,
  size,
  iconSize,
  layouts = DEFAULT_LAYOUTS,
  defaultLayout,
  hideVerticalOnMobile = true,
  orientation = "horizontal",
}: LayoutSelectorProps) => {
  const layout = useAppStoreSelector((state) => state.layout);
  const isMobile = useAppStoreSelector((state) => state.isMobile);
  const dispatch = useAppStoreDispatch();

  const resolvedSize = size ?? (isMobile ? 36 : 50);
  const resolvedIconSize = iconSize ?? (isMobile ? 18 : 25);

  const visibleLayouts = useMemo(() => {
    if (hideVerticalOnMobile && isMobile) {
      return layouts.filter((l) => !isVerticalStripLayout(l));
    }
    return layouts;
  }, [layouts, hideVerticalOnMobile, isMobile]);

  // Fallback when the current layout is no longer visible (e.g. resize to mobile).
  useEffect(() => {
    if (!visibleLayouts.length) return;
    if (visibleLayouts.includes(layout)) return;
    const fallback = defaultLayout && visibleLayouts.includes(defaultLayout)
      ? defaultLayout
      : visibleLayouts[0];
    dispatch("setLayout", fallback);
  }, [visibleLayouts, layout, defaultLayout, dispatch]);

  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: orientation === "vertical" ? "column" : "row",
        gap: "0.5em",
        alignItems: "center",
        ...style,
      }}
    >
      {visibleLayouts.map((l) => {
        const Variant = VARIANT_BY_LAYOUT[l];
        return (
          <Variant
            key={l}
            containerSize={resolvedSize}
            iconSize={resolvedIconSize}
            selected={layout === l}
            onClick={() => dispatch("setLayout", l)}
          />
        );
      })}
    </div>
  );
};
